/**
 * Supabase Auth owns the session (storage, refresh, expiry). Everything else
 * goes through the contracts transport, which asks this module for a token.
 * Signing out or switching user clears every user-scoped cache.
 */
import { createClient, type Session } from '@supabase/supabase-js';
import {readDocumentBytes} from './document-reader.ts';
import { ApiError, DOCUMENTS_BUCKET, answerWithin, uploadLimitMs, LISTING_PHOTOS_BUCKET, createInvitationEmailSender, createTransport, imageFailure, readMySend, type BillingAction, type ImageFailure, type SendState, type Transport, type UploadIo } from '@miraops/contracts';

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

export const configured = Boolean(url && anonKey);

export const supabase = createClient(url ?? 'http://127.0.0.1:56321', anonKey ?? 'missing', {
  auth: { persistSession: true, autoRefreshToken: true, storageKey: 'miraops.auth' },
  // Sign-in, two-factor and session refresh: an answer within 20 s, or it reads as a connection problem.
  global: { fetch: answerWithin(20_000) },
});

/** Refreshes when the token is within 60s of expiry; returns null when signed out. */
async function currentToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  const session = data.session;
  if (!session) return null;
  if (session.expires_at && session.expires_at * 1000 - Date.now() < 60_000) {
    const refreshed = await supabase.auth.refreshSession();
    return refreshed.data.session?.access_token ?? null;
  }
  return session.access_token;
}

/** The executing client, as Activity records it. */
export const CLIENT = { kind: 'web' as const, device: 'MiraOps web' };
export const transport: Transport = createTransport({ url: url ?? '', anonKey: anonKey ?? '', getAccessToken: currentToken, client: CLIENT });
/** Explicit workspace invitation email through the workspace-invite function. */
export const invitationEmail = createInvitationEmailSender({ functionsUrl: `${url ?? ''}/functions/v1`, anonKey: anonKey ?? '', getAccessToken: currentToken });

let activeWorkspaceId: string | null = null;
/** The open workspace; document reads are checked and recorded against it. */
export function setActiveWorkspace(id: string | null) { activeWorkspaceId = id; }

export type { Session };

export type OAuthProvider = 'apple' | 'google' | 'azure';

/** Which external sign-in providers this Auth server has enabled (public GoTrue settings). */
export async function enabledProviders(): Promise<Set<OAuthProvider>> {
  if (!configured) return new Set();
  const response = await answerWithin(10_000)(`${url}/auth/v1/settings`, { headers: { apikey: anonKey! } });
  if (!response.ok) throw new Error(`settings ${response.status}`);
  const body = await response.json() as { external?: Record<string, boolean> };
  return new Set((['apple', 'google', 'azure'] as const).filter(p => body.external?.[p] === true));
}

/**
 * Reads a stored document through the audited document-file route: the member's current
 * access decides every time and the read is recorded in Activity. No public or signed links.
 */
export async function fetchDocument(_bucket: string, path: string, signal?: AbortSignal, purpose: 'preview' | 'view' | 'download' = 'view'): Promise<Blob> {
  const token = await currentToken();
  if (!activeWorkspaceId) throw new Error('no_workspace');
  return readDocumentBytes({endpoint:`${url}/functions/v1/document-file`,anonKey:anonKey!,token,workspaceId:activeWorkspaceId,target:{path},purpose,signal,client:CLIENT});
}

/** Download one exact version in an explicit workspace through current audited authorization. */
export async function fetchDocumentVersion(workspaceId:string,versionId:string,signal?:AbortSignal):Promise<Blob>{
 const token=await currentToken();
 return readDocumentBytes({endpoint:`${url}/functions/v1/document-file`,anonKey:anonKey!,token,workspaceId,target:{version:versionId},purpose:'download',signal,client:CLIENT});
}

/** Upload I/O for the shared upload flow: insert-only Storage write and the trusted verifier. */
export const uploadIo: UploadIo = {
  async putObject(path, bytes, media, signal) {
    const token = await currentToken();
    if (!token) throw new ApiError(401, 'session_expired');
    // Long enough for the whole file at a slow but working connection; longer means it stalled.
    const response = await answerWithin(uploadLimitMs(bytes.length))(`${url}/storage/v1/object/${DOCUMENTS_BUCKET}/${path.split('/').map(encodeURIComponent).join('/')}`, {
      method: 'POST', headers: { apikey: anonKey!, Authorization: `Bearer ${token}`, 'Content-Type': media }, body: bytes, signal,
    });
    // Storage reports an existing object as 400/409 "Duplicate"; the verifier then decides.
    if (response.status === 409 || (response.status === 400 && (await response.clone().text()).includes('Duplicate'))) throw new ApiError(409, 'exists');
    if (!response.ok) throw new ApiError(response.status, 'storage');
  },
  async verify(workspaceId, uploadId, signal) {
    const token = await currentToken();
    if (!token) throw new ApiError(401, 'session_expired');
    const response = await answerWithin(60_000)(`${url}/functions/v1/verify-upload`, {
      method: 'POST', headers: { apikey: anonKey!, Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ workspace_id: workspaceId, upload_id: uploadId }), signal,
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as { code?: string };
      throw new ApiError(response.status, body.code ?? 'verification_failed', body.code);
    }
  },
};

/**
 * Asks the trusted sender to deliver the caller's approved email send, waiting at most
 * 60 seconds. Whatever happens (answer, conflict, timeout, network error) the outcome is
 * read back from the server, so a lost answer never turns an accepted send into a guess.
 */
export async function runEmailSend(workspaceId: string, operationId: string): Promise<{ state: SendState; error?: string }> {
  const token = await currentToken();
  if (!token) throw new ApiError(401, 'session_expired');
  let error: string | undefined;
  try {
    const response = await fetch(`${url}/functions/v1/send-email`, {
      method: 'POST', headers: { apikey: anonKey!, Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ workspace_id: workspaceId, operation_id: operationId }), signal: AbortSignal.timeout(60_000),
    });
    const body = await response.json().catch(() => ({})) as { state?: SendState; code?: string; error?: string };
    if (response.ok && body.state) return { state: body.state, error: body.error ?? undefined };
    error = body.code;
  } catch { error = 'no_answer'; }
  const stored = await readMySend(transport, workspaceId, operationId);
  return { state: stored?.state ?? 'unknown', error };
}

/**
 * Starts the cloud worker for the caller's approved operation. The worker keeps running if
 * this page closes; the receipt (read_operation) is the source of truth, so the answer here
 * is only a hint and any failure is followed by a readback in the Activity view.
 */
export async function runOperation(workspaceId: string, operationId: string): Promise<void> {
  const token = await currentToken();
  if (!token) throw new ApiError(401, 'session_expired');
  try {
    await fetch(`${url}/functions/v1/operations-worker`, {
      method: 'POST', headers: { apikey: anonKey!, Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ workspace_id: workspaceId, operation_id: operationId }), signal: AbortSignal.timeout(120_000),
    });
  } catch { /* the receipt shows what happened */ }
}

/** Where external AI applications connect (MCP over HTTP). */
export const mcpEndpoint = `${url ?? ''}/functions/v1/mcp`;

/** Verifies an AI connection or refreshes its usage through the adapter function. */
export async function aiConnectionAction(workspaceId: string, connectionId: string, action: 'verify' | 'refresh_usage'): Promise<{ outcome?: string; code?: string | null; refreshed?: boolean; error?: string }> {
  const token = await currentToken();
  if (!token) throw new ApiError(401, 'session_expired');
  try {
    const response = await fetch(`${url}/functions/v1/ai-connections`, {
      method: 'POST', headers: { apikey: anonKey!, Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ workspace_id: workspaceId, connection_id: connectionId, action }), signal: AbortSignal.timeout(30_000),
    });
    const body = await response.json().catch(() => ({})) as { outcome?: string; code?: string | null; refreshed?: boolean };
    return response.ok ? body : { error: body.code ?? `http_${response.status}` };
  } catch { return { error: 'no_answer' }; }
}

/** Starts the cloud worker for the caller's queued analysis; the analysis record is the source of truth. */
export async function runAnalysis(workspaceId: string, analysisId: string): Promise<void> {
  const token = await currentToken();
  if (!token) throw new ApiError(401, 'session_expired');
  try {
    await fetch(`${url}/functions/v1/analysis-worker`, {
      method: 'POST', headers: { apikey: anonKey!, Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ workspace_id: workspaceId, analysis_id: analysisId }), signal: AbortSignal.timeout(120_000),
    });
  } catch { /* the review page reads the analysis state */ }
}

async function functionCall(path: string, init: RequestInit = {}): Promise<Response> {
  const token = await currentToken();
  if (!token) throw new ApiError(401, 'session_expired');
  // Always an answer within 60 s, also when the caller passes its own cancel signal.
  return answerWithin(60_000)(`${url}/functions/v1/${path}`, { ...init, headers: { apikey: anonKey!, Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', ...(init.headers ?? {}) } });
}
/** Workspace plan billing with Stripe (billing function): checkout and portal answer a Stripe page
 *  to open; seats and refresh answer once the Stripe subscription has been read back. A refusal
 *  throws with its code (billing_not_configured, price_not_set, already_subscribed …). */
export async function billingAction(workspaceId: string, action: BillingAction, options: { plan?: string; returnPath?: string } = {}):
  Promise<{ url?: string; result?: string; seats?: number }> {
  const r = await functionCall('billing', { method: 'POST', body: JSON.stringify({ workspace_id: workspaceId, action, plan_code: options.plan, return_path: options.returnPath }),
    signal: AbortSignal.timeout(45_000) });
  const body = await r.json().then((b: unknown) => b as Record<string, unknown>, () => ({} as Record<string, unknown>));
  if (!r.ok) throw new ApiError(r.status, typeof body.code === 'string' ? body.code : 'billing_failed');
  return body as { url?: string; result?: string; seats?: number };
}
/** Starts connecting the person's own Gmail or Outlook mailbox: the provider's sign-in page. */
export async function mailboxConnect(workspaceId: string, provider: 'gmail' | 'outlook'): Promise<string> {
  const r = await functionCall('mailbox-connect', { method: 'POST', body: JSON.stringify({ workspace_id: workspaceId, provider }), signal: AbortSignal.timeout(30_000) });
  const body = await r.json().then((b: unknown) => b as Record<string, unknown>, () => ({} as Record<string, unknown>));
  if (!r.ok || typeof body.url !== 'string') throw new ApiError(r.status, typeof body.code === 'string' ? body.code : 'mailbox_failed');
  return body.url;
}
/** Matches a listing's photos to the auction now (listing-photo-sync). Counts of what changed;
 *  a refusal or an unreachable auction throws with its code, and nothing stored is changed. */
export async function refreshListingPhotos(workspaceId: string, occurrence: string, signal?: AbortSignal):
  Promise<{ added: number; removed: number; replaced: number; unchanged: number }> {
  const r = await functionCall('listing-photo-sync', { method: 'POST', body: JSON.stringify({ workspace_id: workspaceId, occurrence_id: occurrence }),
    signal: signal ? AbortSignal.any([signal, AbortSignal.timeout(30_000)]) : AbortSignal.timeout(30_000) });
  const body = await r.json().then((b: unknown) => b as Record<string, unknown>, () => ({} as Record<string, unknown>));
  if (!r.ok) throw new ApiError(r.status, typeof body.code === 'string' ? body.code : 'refresh_failed');
  return body as { added: number; removed: number; replaced: number; unchanged: number };
}
/** One auction listing photo. A public copy (decision 8) loads from its plain link with no
 *  sign-in; an older private one goes through the access-checked function. Failures say why: not
 *  allowed (401/403) is kept apart from expired, throttled and temporarily unavailable. */
export class ImageLoadError extends Error { readonly failure: ImageFailure; constructor(failure: ImageFailure) { super(failure); this.failure = failure; } }
export async function listingImage(workspaceId: string, occurrence: string, seq: number, signal?: AbortSignal, publicPath?: string | null): Promise<Blob> {
  const timeout = AbortSignal.timeout(20_000);
  const both = signal ? AbortSignal.any([signal, timeout]) : timeout;
  let r: Response;
  try {
    r = publicPath ? await fetch(`${url}/storage/v1/object/public/${LISTING_PHOTOS_BUCKET}/${publicPath.split('/').map(encodeURIComponent).join('/')}`, { signal: both })
      : await functionCall(`listing-image?w=${workspaceId}&o=${occurrence}&seq=${seq}`, { method: 'GET', signal: both });
  }
  catch (error) { if (signal?.aborted) throw error; throw new ImageLoadError('unavailable'); }
  if (!r.ok) throw new ImageLoadError(imageFailure(r.status, r.status === 410 ? await r.json().then((b: { code?: unknown }) => typeof b?.code === 'string' ? b.code : null, () => null) : null)); // ignored: an unreadable error body falls back to the status
  return r.blob();
}
/** A portal participant's granted file copy. */
export async function portalFile(invitationId: string, versionId: string): Promise<Blob> {
  const r = await functionCall(`portal-file?i=${invitationId}&v=${versionId}`, { method: 'GET' });
  if (!r.ok) throw new ApiError(r.status, 'portal_file_denied');
  return r.blob();
}
/** Deletes the caller's own account (account-delete function), then forgets the session on this
 *  device. A refusal or failure returns its code; a retry should reuse the same request ID. */
export async function deleteMyAccount(requestId: string): Promise<{ ok: boolean; code?: string }> {
  const r = await functionCall('account-delete', { method: 'POST', body: JSON.stringify({ request_id: requestId, confirm: 'DELETE' }) });
  const body = await r.json().catch(() => ({})) as { code?: string };
  if (r.ok) await supabase.auth.signOut({ scope: 'local' }).catch(() => undefined); // ignored: the account is already deleted; the session is unusable
  return { ok: r.ok, code: body.code };
}
/** Sends a portal invitation email (explicit staff action). */
export async function sendPortalInvitation(workspaceId: string, invitationId: string, expected: number): Promise<{ ok: boolean; code?: string }> {
  const r = await functionCall('portal-invite', { method: 'POST', body: JSON.stringify({ workspace_id: workspaceId, invitation_id: invitationId, expected }) });
  const body = await r.json().catch(() => ({})) as { code?: string };
  return { ok: r.ok, code: body.code };
}
/** Local test billing provider (never real billing). */
export async function simulateBilling(workspaceId: string, subscriptionId: string, action: 'pay' | 'renew' | 'fail_payment' | 'cancel'): Promise<void> {
  const r = await functionCall('billing-webhook/simulate', { method: 'POST', body: JSON.stringify({ workspace_id: workspaceId, subscription_id: subscriptionId, action }) });
  if (!r.ok) throw new ApiError(r.status, 'billing_simulation_failed');
}

/** Starts the export worker for queued activity exports (it also resumes stalled ones). */
export async function kickAuditExports(): Promise<void> {
  const token = await currentToken();
  if (!token) return;
  await answerWithin(10_000)(`${url}/functions/v1/audit-export`, { method: 'POST', headers: { apikey: anonKey!, Authorization: `Bearer ${token}` } }).catch(() => undefined); // ignored: a nudge only; queued exports stay listed and resume on the next start
}

/** Downloads a ready export; the server rechecks permission and expiry and records it. */
export async function auditExportDownload(workspaceId: string, exportId: string): Promise<Blob> {
  const token = await currentToken();
  if (!token) throw new Error('signed_out');
  const response = await answerWithin(30_000)(`${url}/functions/v1/audit-export?w=${workspaceId}&e=${exportId}`, {
    headers: { apikey: anonKey!, Authorization: `Bearer ${token}`, 'x-miraops-client': CLIENT.kind, 'x-miraops-device': CLIENT.device },
  });
  if (response.status === 410) throw new Error('export_unavailable');
  if (!response.ok) throw new ApiError(response.status, 'export_download');
  return response.blob();
}
